import {
  SettingsType,
  type SettingInfoDto,
} from '../../../shared/api/pipeex/pcfSettingsClient';
import { DEFAULT_PIPEEX_PROFILE_NAME } from '../../../shared/config/defaultPipeexProfile/defaultPipeexProfile';
import { buildProfileKey } from './profile';

/**
 * Pure selection-resolution policy for the mapping profile list. Given the
 * freshly loaded profiles and every selection hint (previous selection,
 * project-preferred profile, active profile, flow-snapshot marker), it decides
 * which profile key the workspace should select. The hook layer applies the
 * decision to React state; keeping the policy pure makes the branch-dense
 * priority chain directly testable.
 */

export interface ResolveProfileSelectionInput {
  profiles: SettingInfoDto[];
  currentSelectedProfileKey: string;
  /** Built from getActivePipeexProfile(); empty string when no active profile. */
  activeProfileKey: string;
  /** Empty string when the project has no preferred profile. */
  projectPreferredProfileKey: string;
  requireExplicitProfileSelection: boolean;
  enableFlowSnapshot: boolean;
  restoreExistingProfileOnMount: boolean;
  flowSnapshotSaved: boolean;
}

export type ProfileSelectionResolution =
  | { kind: 'empty-list' }
  | { kind: 'cleared-for-project-gate'; debugPayload: Record<string, unknown> }
  | {
      kind: 'resolved';
      nextProfileKey: string;
      debugPayload: Record<string, unknown>;
    };

export function resolveProfileSelection({
  profiles,
  currentSelectedProfileKey,
  activeProfileKey,
  projectPreferredProfileKey,
  requireExplicitProfileSelection,
  enableFlowSnapshot,
  restoreExistingProfileOnMount,
  flowSnapshotSaved,
}: ResolveProfileSelectionInput): ProfileSelectionResolution {
  if (profiles.length === 0) {
    return { kind: 'empty-list' };
  }

  const profileKeys = profiles.map((profile) => buildProfileKey(profile.name, profile.settingsType));
  const defaultProfile =
    profiles.find(
      (profile) =>
        profile.name === DEFAULT_PIPEEX_PROFILE_NAME &&
        profile.settingsType === SettingsType.User
    ) ?? profiles[0];
  const defaultProfileKey = buildProfileKey(defaultProfile.name, defaultProfile.settingsType);

  const projectPreferredExists =
    projectPreferredProfileKey !== '' && profileKeys.includes(projectPreferredProfileKey);
  const activeExists = activeProfileKey !== '' && profileKeys.includes(activeProfileKey);
  const previousExists =
    currentSelectedProfileKey !== '' && profileKeys.includes(currentSelectedProfileKey);

  if (
    requireExplicitProfileSelection &&
    !previousExists &&
    !projectPreferredExists
  ) {
    return {
      kind: 'cleared-for-project-gate',
      debugPayload: {
        profileKeys,
        projectPreferredProfileKey,
        activeProfileKey,
      },
    };
  }

  const shouldPreferFlowSnapshotProfile =
    enableFlowSnapshot &&
    !restoreExistingProfileOnMount &&
    flowSnapshotSaved &&
    !previousExists;

  const nextProfileKey = previousExists
    ? currentSelectedProfileKey
    : projectPreferredExists
      ? projectPreferredProfileKey
      : shouldPreferFlowSnapshotProfile
        ? defaultProfileKey
        : activeExists
          ? activeProfileKey
          : defaultProfileKey;

  return {
    kind: 'resolved',
    nextProfileKey,
    debugPayload: {
      profileKeys,
      defaultProfileKey,
      projectPreferredProfileKey,
      projectPreferredExists,
      activeProfileKey,
      activeExists,
      previousExists,
      shouldPreferFlowSnapshotProfile,
      nextProfileKey,
    },
  };
}
